// Compra de un producto digital desde el catálogo público: el cliente deja su
// nombre, celular y correo (así el negocio sabe quién es y a quién buscar en su
// plataforma de pago), se registra la compra como "por confirmar" y se le lleva
// al link de pago del vendedor (ej. Hotmart).

import React, { useState } from 'react';
import { FaTimes, FaLock, FaExternalLinkAlt, FaCheckCircle } from 'react-icons/fa';
import { comprarProductoDigital } from '../services/apiService';
import { formatCOP } from '../utils/formatters';
import AutorizacionDatos, { AUTORIZACION_FALTANTE_MSG } from './AutorizacionDatos';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// En pestaña nueva (no window.location.href): el pago es en el sitio del
// vendedor (ej. Hotmart), y sacar al comprador de la vitrina le hace perder
// de vista el catálogo — y, si tenía más productos elegidos, el carrito.
// noopener+noreferrer: el sitio externo (lo escribe el vendedor, no lo
// controlamos) nunca debe poder tocar esta pestaña.
const irAlPago = (url) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

// `onRedirect` se puede sustituir (ej. en pruebas); por defecto abre el link de pago en una pestaña nueva.
// `tienda`: responsable de los datos del comprador (nombre y contacto), para la autorización y su política.
const DigitalPurchaseModal = ({ slug, codigoVendedor, product, tienda, onClose, onRedirect = irAlPago }) => {
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [email, setEmail] = useState('');
  const [autorizaDatos, setAutorizaDatos] = useState(false);
  const [aceptaPromociones, setAceptaPromociones] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  // Con el link ya entregado: la compra quedó registrada y la pestaña de pago
  // se abrió (o el navegador la bloqueó, por eso se deja el enlace a mano).
  const [linkPago, setLinkPago] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!nombre.trim() || !telefono.trim()) {
      setError('Tu nombre y celular son obligatorios.');
      return;
    }
    if (!EMAIL_REGEX.test(email.trim())) {
      setError('Escribe un correo válido: es el que usarás para recibir tu acceso.');
      return;
    }
    if (!autorizaDatos) {
      setError(AUTORIZACION_FALTANTE_MSG);
      return;
    }

    setEnviando(true);
    try {
      const resultado = await comprarProductoDigital(slug, product.id, {
        nombre: nombre.trim(),
        telefono: telefono.trim(),
        email: email.trim(),
      }, codigoVendedor, { autorizacionDatos: true, aceptaPromociones });
      onRedirect(resultado.linkCompra);
      setLinkPago(resultado.linkCompra);
    } catch (err) {
      setError(err.message || 'No se pudo registrar la compra.');
    } finally {
      setEnviando(false);
    }
  };

  const campo = 'w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-base outline-none focus:ring-2 focus:ring-purple-500';

  // Compra ya registrada: se muestra el resultado en vez del formulario, con
  // el enlace a mano por si el navegador bloqueó la pestaña nueva.
  if (linkPago) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-end md:items-center justify-center p-0 md:p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div role="dialog" aria-modal="true" aria-label={`Compra registrada — ${product.nombre}`} className="bg-white rounded-t-[2rem] md:rounded-[2rem] shadow-2xl w-full max-w-md p-6 text-center">
          <FaCheckCircle className="mx-auto text-emerald-500 mb-3" size={40} />
          <h2 className="text-lg font-black text-gray-800 mb-1">¡Listo! Abrimos tu página de pago en otra pestaña</h2>
          <p className="text-sm text-gray-500 mb-5">
            Si no se abrió sola (a veces el navegador la bloquea), tócala aquí abajo. Puedes volver al catálogo cuando quieras, esta pestaña sigue abierta.
          </p>
          <a
            href={linkPago}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-3.5 bg-purple-600 hover:bg-purple-700 text-white font-black rounded-xl flex items-center justify-center gap-2 active:scale-95 transition-all mb-3"
          >
            <FaExternalLinkAlt size={12} /> Ir a la página de pago
          </a>
          <button type="button" onClick={onClose} className="w-full py-3 text-sm font-bold text-gray-500 hover:text-gray-700">
            Volver al catálogo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-end md:items-center justify-center p-0 md:p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !enviando) onClose(); }}
    >
      <form
        onSubmit={handleSubmit}
        role="dialog"
        aria-modal="true"
        aria-label={`Comprar ${product.nombre}`}
        className="bg-white rounded-t-[2rem] md:rounded-[2rem] shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-3 px-6 pt-6">
          <div>
            <h2 className="text-lg font-black text-gray-800 leading-tight">{product.nombre}</h2>
            <p className="text-2xl font-black text-purple-600">{formatCOP(product.precioVenta)}</p>
          </div>
          <button type="button" onClick={onClose} disabled={enviando} aria-label="Cerrar" className="p-2 rounded-xl text-gray-400 hover:bg-gray-100 disabled:opacity-40">
            <FaTimes />
          </button>
        </div>

        <div className="px-6 py-4 space-y-3">
          <p className="text-sm text-gray-500">
            Déjanos tus datos para que el negocio sepa quién eres y pueda entregarte tu acceso. Vas a pagar en una pestaña nueva, sin salir del catálogo.
          </p>

          <div>
            <label htmlFor="dp-nombre" className="text-[10px] font-black text-gray-400 uppercase">Tu nombre</label>
            <input id="dp-nombre" type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} autoComplete="name" className={campo} />
          </div>
          <div>
            <label htmlFor="dp-telefono" className="text-[10px] font-black text-gray-400 uppercase">Tu celular</label>
            <input id="dp-telefono" type="tel" value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="3001234567" autoComplete="tel" className={campo} />
          </div>
          <div>
            <label htmlFor="dp-email" className="text-[10px] font-black text-gray-400 uppercase">Tu correo</label>
            <input id="dp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tucorreo@ejemplo.com" autoComplete="email" className={campo} />
            <p className="text-[10px] text-gray-400 mt-1">Usa el mismo correo con el que vas a pagar: ahí recibirás tu acceso.</p>
          </div>

          <AutorizacionDatos
            tienda={tienda}
            autoriza={autorizaDatos}
            onAutoriza={setAutorizaDatos}
            promociones={aceptaPromociones}
            onPromociones={setAceptaPromociones}
            idPrefix="dp"
          />

          {error && <p role="alert" className="text-red-500 text-xs font-bold">{error}</p>}
        </div>

        <div className="px-6 pb-6">
          <button
            type="submit"
            disabled={enviando}
            className="w-full py-3.5 bg-purple-600 hover:bg-purple-700 text-white font-black rounded-xl flex items-center justify-center gap-2 disabled:opacity-60 active:scale-95 transition-all"
          >
            <FaLock size={12} /> {enviando ? 'Registrando tu compra…' : 'Continuar al pago'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DigitalPurchaseModal;
